/**
 * The public /privacy and /terms pages: readable without signing in (Google's
 * review and new sign-ups need them), in the SynAnalytics brand.
 */

import { useEffect, type ReactNode } from 'react';

import { PrivacyPolicy, PRIVACY_UPDATED } from './privacy.tsx';
import { TermsOfService, TERMS_UPDATED } from './terms.tsx';

export type LegalDocument = 'privacy' | 'terms';

const PAGES: Record<LegalDocument, { title: string; updated: string; body: () => ReactNode }> = {
  privacy: { title: 'Privacy Policy', updated: PRIVACY_UPDATED, body: PrivacyPolicy },
  terms: { title: 'Terms of Service', updated: TERMS_UPDATED, body: TermsOfService },
};

/** Which legal page this address is, if any. */
export function legalDocumentFromUrl(): LegalDocument | null {
  const path = window.location.pathname.replace(/\/+$/, '');
  if (path === '/privacy') return 'privacy';
  if (path === '/terms') return 'terms';
  return null;
}

export function LegalPage({ document: which }: { document: LegalDocument }): ReactNode {
  const page = PAGES[which];
  useEffect(() => {
    document.title = `${page.title} · SynAnalytics`;
  }, [page.title]);
  const Body = page.body;
  return (
    <div className="legal">
      <header className="legal-head">
        <a href="/" className="legal-brand">
          SynAnalytics
        </a>
        <nav aria-label="Legal">
          <a href="/privacy" aria-current={which === 'privacy' ? 'page' : undefined}>
            Privacy
          </a>
          <a href="/terms" aria-current={which === 'terms' ? 'page' : undefined}>
            Terms
          </a>
        </nav>
      </header>
      <main className="legal-body">
        <h1>{page.title}</h1>
        <p className="legal-updated">Last updated {page.updated}</p>
        <Body />
      </main>
      <footer className="legal-foot">© 2026 Syntera Labs · South Africa</footer>
    </div>
  );
}
